import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Grid,
  Container,
  InputAdornment,
  Avatar,
} from "@mui/material";
import { Plus, Search } from "lucide-react";
import UserTable from "./UserTable";
import UserForm from "./UserForm";
import apiEndPointsConfig from "../../../services/apiEndPoints";
import axiosInstance from "../../../services/axiosInstance";

export default function AllUser() {
  const role = localStorage.getItem("role");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const [openForm, setOpenForm] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [filters, setFilters] = useState({
    search: "",
    role: "",
    status: "",
  });

  // Fetch all admins on component mount
  useEffect(() => {
    fetchAllAdmins();
  }, []);

  const fetchAllAdmins = async () => {
    try {
      setLoading(true);
      setError("");

      const token = localStorage.getItem("token"); // or from auth context

      const response = await axiosInstance.get(apiEndPointsConfig.getAllAdmins);

      setUsers(response?.data?.admins || []);
    } catch (err) {
      const message =
        err?.response?.data?.message || err?.message || "Error fetching admins";

      setError(message);
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter((user) => {
    const fullName = user.fullName ? user.fullName.toLowerCase() : "";
    const email = user.email ? user.email.toLowerCase() : "";
    const search = filters.search.toLowerCase();

    return (
      (filters.search === "" ||
        fullName.includes(search) ||
        email.includes(search)) &&
      (filters.role === "" || user.role === filters.role) &&
      (filters.status === "" || user.status === filters.status)
    );
  });

  const openAddForm = () => {
    setEditMode(false);
    setSelectedUser(null);
    setOpenForm(true);
  };

  const openEditForm = (user) => {
    setEditMode(true);
    setSelectedUser({
      fullName: user.fullName,
      email: user.email,
      password: "",
      role: user.role,
      status: user.status,
      _id: user._id,
    });
    setOpenForm(true);
  };

  const handleFormSubmit = async (values, resetForm) => {
    try {
      setLoading(true);
      setError("");

      if (editMode) {
        // Update user
        const updateData = {
          fullName: values.fullName,
          email: values.email,
          role: values.role,
          status: values.status,
        };

        if (values.password) {
          updateData.password = values.password;
        }

        const response = await axiosInstance.put(
          `${apiEndPointsConfig.updateAdmin}/${values._id}`,
          updateData
        );

        setSuccessMessage("User updated successfully");
        setUsers((prev) =>
          prev.map((u) => (u._id === values._id ? { ...u, ...updateData } : u))
        );
      } else {
        // Create new user
        const response = await axiosInstance.post(`${apiEndPointsConfig.createAdmin}`, {
          fullName: values.fullName,
          email: values.email,
          password: values.password,
          role: values.role,
          status: values.status,
        });

        const data = response.data;
        setUsers((prev) => [...prev, data.admin]);
        setSuccessMessage("User created successfully");
      }

      setOpenForm(false);
      resetForm();
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      setError(err.message || "Error processing user");
      console.error("Submit error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteTarget(id);
    setDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      setError("");

      await axiosInstance.delete(
        `${apiEndPointsConfig.deleteAdmin}/${deleteTarget}`
      );

      setUsers((prev) => prev.filter((u) => u._id !== deleteTarget));
      setSuccessMessage("User deleted successfully");
      setDeleteDialog(false);
      setDeleteTarget(null);

      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      setError(err.message || "Error deleting user");
      console.error("Delete error:", err);
    } finally {
      setLoading(false);
    }
  };

  const stats = [
    {
      label: "Total Users",
      value: users.length,
      color: "#3b82f6",
      bgColor: "#eff6ff",
    },
    {
      label: "Active",
      value: users.filter((u) => u.status === "active").length,
      color: "#10b981",
      bgColor: "#ecfdf5",
    },
    {
      label: "Inactive",
      value: users.filter((u) => u.status === "inactive").length,
      color: "#f59e0b",
      bgColor: "#fffbeb",
    },
  ];

  return (
    <Box
      sx={{
        width: "100%",
        background: "linear-gradient(135deg, #f5f7fa 0%, #fdfdfdff 100%)",
        minHeight: "100vh",
        py: { xs: 2, md: 3 },
        px: { xs: 1, md: 0 },
      }}
    >
      <Container maxWidth="xl">
        {/* Error & Success Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        {successMessage && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccessMessage("")}
          >
            {successMessage}
          </Alert>
        )}

        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {stats.map((stat, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Grid item xs={12} sm={12} md={12} key={i}>
                <Card
                  sx={{
                    borderRadius: "16px",
                    background: "white",
                    border: "1px solid #e5e7eb",
                    boxShadow: "0 4px 6px rgba(0,0,0,0.05)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 12px 24px rgba(0,0,0,0.1)",
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Avatar
                        sx={{
                          width: 56,
                          height: 56,
                          bgcolor: stat.bgColor,
                          color: stat.color,
                        }}
                      >
                        {stat.icon}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#6b7280",
                            fontWeight: 500,
                            mb: 0.5,
                          }}
                        >
                          {stat.label}
                        </Typography>
                        <Typography
                          variant="h4"
                          sx={{
                            fontWeight: 700,
                            color: stat.color,
                          }}
                        >
                          {stat.value}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          ))}
        </Grid>

        {/* Filters & Controls */}
        <Paper
          sx={{
            p: 3,
            borderRadius: "12px",
            background: "white",
            border: "1px solid #e5e7eb",
            mb: 3,
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              flexWrap: { xs: "wrap", sm: "nowrap" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                flex: 1,
              }}
            >
              <TextField
                placeholder="Search by name or email..."
                size="small"
                sx={{
                  width: { xs: "100%", sm: 250 },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    backgroundColor: "#f9fafb",
                    border: "1px solid #e5e7eb",
                    transition: "all 0.2s",
                    "&:hover": {
                      backgroundColor: "#f3f4f6",
                    },
                    "&.Mui-focused": {
                      backgroundColor: "white",
                      borderColor: "#3b82f6",
                      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={18} color="#9ca3af" />
                    </InputAdornment>
                  ),
                }}
                onChange={(e) =>
                  setFilters({ ...filters, search: e.target.value })
                }
              />

              <Autocomplete
                size="small"
                options={[
                  { label: "Admin", value: "admin" },
                  { label: "Account Manager", value: "accountManager" },
                  { label: "Agent", value: "agent" },
                  { label: "Viewer", value: "viewer" },
                ]}
                getOptionLabel={(option) => option.label}
                value={
                  [
                    { label: "Admin", value: "admin" },
                    { label: "Account Manager", value: "accountManager" },
                    { label: "Agent", value: "agent" },
                    { label: "Viewer", value: "viewer" },
                  ].find((r) => r.value === filters.role) || {
                    label: "All Roles",
                    value: "",
                  }
                }
                onChange={(e, newValue) =>
                  setFilters({ ...filters, role: newValue?.value || "" })
                }
                sx={{
                  width: { xs: "100%", sm: 140 },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    background: "#f9fafb",
                  },
                }}
                renderInput={(params) => <TextField {...params} label="Role" />}
              />

              <Autocomplete
                size="small"
                options={[
                  { label: "Active", value: "active" },
                  { label: "Inactive", value: "inactive" },
                  { label: "Deleted", value: "deleted" },
                ]}
                getOptionLabel={(option) => option.label}
                value={
                  [
                    { label: "Active", value: "active" },
                    { label: "Inactive", value: "inactive" },
                    { label: "Deleted", value: "deleted" },
                  ].find((s) => s.value === filters.status) || {
                    label: "All Status",
                    value: "",
                  }
                }
                onChange={(e, newValue) =>
                  setFilters({ ...filters, status: newValue?.value || "" })
                }
                sx={{
                  width: { xs: "100%", sm: 140 },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    background: "#f9fafb",
                  },
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Status" />
                )}
              />
            </Box>

            <Button
              variant="contained"
              startIcon={<Plus size={20} />}
              onClick={openAddForm}
              // disabled={loading}
              disabled={role !== "admin" && role !== "superadmin"}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                bgcolor: "#6366F1",
                "&:hover": {
                  bgcolor: "#1d4ed8",
                },
                transition: "all 0.3s",
                "&:hover": {
                  boxShadow: "0 6px 16px rgba(59, 130, 246, 0.4)",
                  transform: "translateY(-1px)",
                },
                whiteSpace: "nowrap",
              }}
            >
              Add User
            </Button>
          </Box>
        </Paper>

        {/* Users Table */}
        <UserTable
          users={filteredUsers}
          loading={loading}
          onEdit={openEditForm}
          onDelete={handleDeleteClick}
        />

        {/* Add/Edit User Form */}
        <UserForm
          open={openForm}
          onClose={() => setOpenForm(false)}
          editMode={editMode}
          initialValues={selectedUser}
          onSubmit={handleFormSubmit}
          loading={loading}
        />

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
          <DialogTitle
            sx={{
              fontWeight: 600,
              color: "#1f2937",
            }}
          >
            Delete User
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ color: "#6b7280", mt: 1 }}>
              Are you sure you want to delete this user? This action cannot be
              undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => setDeleteDialog(false)}
              disabled={loading}
              sx={{ color: "#6b7280" }}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmDelete}
              disabled={loading}
              variant="contained"
              sx={{
                background: "#ef4444",
                "&:hover": { background: "#dc2626" },
              }}
            >
              {loading ? "Deleting..." : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}
